// src/pages/Connexion.js
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import BoutonGoogle, { Separateur } from "../components/BoutonGoogle";

export default function Connexion() {
  const { connecter, resetPassword } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = React.useState(null);
  const isStandalone = window.matchMedia("(display-mode: standalone)").matches;
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const showInstall = !isStandalone;

  React.useEffect(() => {
    window.addEventListener("beforeinstallprompt", (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    });
  }, []);

  async function handleInstall() {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    }
  }

  async function handleLogin(e) {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await connecter(form.email.trim(), form.password);
      navigate("/");
    } catch (e) {
      if (e.code === "auth/invalid-credential" || e.code === "auth/wrong-password") {
        setError("Email ou mot de passe incorrect. Si vous avez créé votre compte avec Google, utilisez le bouton « Continuer avec Google ».");
      } else if (e.code === "auth/user-not-found") {
        setError("Aucun compte trouvé avec cet email.");
      } else if (e.code === "auth/too-many-requests") {
        setError("Trop de tentatives. Réessayez dans quelques minutes.");
      } else {
        setError("Erreur de connexion. Réessayez.");
      }
    }
    setLoading(false);
  }

  async function handleReset(e) {
    e.preventDefault();
    if (!form.email.trim()) { setError("Entrez votre email."); return; }
    setLoading(true);
    try {
      await resetPassword(form.email.trim());
      setResetSent(true);
    } catch {
      setError("Erreur. Vérifiez l'email saisi.");
    }
    setLoading(false);
  }

  return (
    <div style={s.wrapper}>
      <div style={s.card}>
        {/* Logo */}
        <div style={s.header}>
          <a href="https://tombo.live" style={s.logoLink}>
            <img src="/logo_app.png" alt="TomboLive" style={s.logoImg} />
          </a>
          <h1 style={s.title}>{resetMode ? "Réinitialiser le mot de passe" : "Connexion"}</h1>
          {!resetMode && <p style={s.subtitle}>Bon retour parmi nous 👋</p>}
        </div>

        {resetSent ? (
          <div style={s.successBox}>
            <div style={s.successIcon}>📧</div>
            <p style={s.successText}>Email de réinitialisation envoyé à <strong>{form.email}</strong>.</p>
            <button onClick={() => { setResetMode(false); setResetSent(false); }} style={s.linkBtn}>
              ← Retour à la connexion
            </button>
          </div>
        ) : (
          <>
          {!resetMode && (
            <>
              <BoutonGoogle onUser={() => navigate("/")} onError={setError} />
              <Separateur texte="ou avec votre email" />
            </>
          )}
          <form onSubmit={resetMode ? handleReset : handleLogin} style={s.form}>
            <div style={s.field}>
              <label style={s.label}>✉️ Email</label>
              <input type="email" placeholder="votre@email.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                style={s.input} required />
            </div>

            {!resetMode && (
              <div style={s.field}>
                <label style={s.label}>🔒 Mot de passe</label>
                <div style={s.passwordRow}>
                  <input type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    style={{ ...s.input, flex:1, marginBottom:0 }} required />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} style={s.eyeBtn}>
                    {showPassword ? "🙈" : "👁️"}
                  </button>
                </div>
              </div>
            )}

            {error && <div style={s.errBox}>⚠️ {error}</div>}

            <button type="submit" disabled={loading} style={s.btnPrimary}>
              {loading ? "…" : resetMode ? "📧 Envoyer le lien" : "🔓 Se connecter"}
            </button>

            <button type="button" onClick={() => { setResetMode(!resetMode); setError(""); }} style={s.linkBtn}>
              {resetMode ? "← Retour à la connexion" : "Mot de passe oublié ?"}
            </button>
          </form>
          </>
        )}

        {!resetMode && !resetSent && (
          <p style={s.signupLink}>
            Pas encore de compte ?{" "}
            <Link to="/inscription" style={s.link}>Créer un compte gratuit</Link>
          </p>
        )}

        {/* Bandeau installation PWA */}
        {(showInstall || isIOS) && (
          <div style={s.installBox}>
            <div style={s.installIcon}>📲</div>
            <div style={s.installContent}>
              <div style={s.installTitle}>Installer l'application</div>
              {isIOS ? (
                <div style={s.installText}>
                  Appuyez sur <strong>⬆️ Partager</strong> puis <strong>"Sur l'écran d'accueil"</strong>
                </div>
              ) : (
                <div style={s.installText}>
                  Accédez à TomboLive en un tap depuis votre écran d'accueil
                </div>
              )}
            </div>
            {!isIOS && (
              <button onClick={handleInstall} style={s.installBtn}>
                Installer
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  wrapper: { minHeight:"100vh", background:"linear-gradient(135deg,#1a3a8f,#764ba2)", display:"flex", alignItems:"center", justifyContent:"center", padding:"2rem 1rem" },
  card: { background:"#fff", borderRadius:"20px", width:"100%", maxWidth:"420px", padding:"2rem", boxShadow:"0 20px 60px rgba(0,0,0,0.2)" },
  header: { textAlign:"center", marginBottom:"1.75rem" },
  logoLink: { display:"inline-flex", alignItems:"center", justifyContent:"center", textDecoration:"none", marginBottom:"0.5rem" },
  logoImg: { width:"160px", height:"160px", objectFit:"contain" },
  title: { fontSize:"1.5rem", fontWeight:"800", color:"#1a1a2e", marginBottom:"0.25rem" },
  subtitle: { fontSize:"0.85rem", color:"#888" },
  form: { display:"flex", flexDirection:"column", gap:"1rem" },
  field: { display:"flex", flexDirection:"column", gap:"6px" },
  label: { fontSize:"0.85rem", fontWeight:"600", color:"#444" },
  input: { padding:"0.7rem 1rem", border:"1.5px solid #e0e0e0", borderRadius:"10px", fontSize:"0.95rem", outline:"none", width:"100%", boxSizing:"border-box" },
  passwordRow: { display:"flex", gap:"8px", alignItems:"center" },
  eyeBtn: { background:"#f5f5f5", border:"1.5px solid #e0e0e0", borderRadius:"8px", padding:"0.65rem 0.75rem", cursor:"pointer", fontSize:"1rem", flexShrink:0 },
  errBox: { background:"#ffebee", color:"#b71c1c", borderRadius:"10px", padding:"0.75rem 1rem", fontSize:"0.85rem" },
  btnPrimary: { padding:"0.9rem", background:"linear-gradient(135deg,#667eea,#764ba2)", color:"#fff", border:"none", borderRadius:"12px", fontSize:"1rem", fontWeight:"800", cursor:"pointer" },
  linkBtn: { background:"none", border:"none", color:"#667eea", fontSize:"0.85rem", cursor:"pointer", padding:0, fontWeight:"600", textAlign:"center" },
  successBox: { textAlign:"center", padding:"1rem 0" },
  successIcon: { fontSize:"2.5rem", marginBottom:"0.75rem" },
  successText: { fontSize:"0.9rem", color:"#555", marginBottom:"1rem", lineHeight:1.6 },
  signupLink: { textAlign:"center", marginTop:"1.25rem", fontSize:"0.85rem", color:"#888" },
  link: { color:"#667eea", textDecoration:"none", fontWeight:"600" },
  installBox: { marginTop:"1.25rem", background:"linear-gradient(135deg,#eef0ff,#f5f0ff)", borderRadius:"14px", padding:"0.875rem 1rem", display:"flex", alignItems:"center", gap:"10px", border:"1.5px solid #c7d2ff" },
  installIcon: { fontSize:"1.5rem", flexShrink:0 },
  installContent: { flex:1 },
  installTitle: { fontWeight:"800", color:"#1a3a8f", fontSize:"0.85rem" },
  installText: { color:"#555", fontSize:"0.78rem", lineHeight:1.5, marginTop:"2px" },
  installBtn: { background:"linear-gradient(135deg,#667eea,#764ba2)", color:"#fff", border:"none", borderRadius:"8px", padding:"6px 14px", fontWeight:"700", fontSize:"0.8rem", cursor:"pointer", flexShrink:0 },
};
